import Vue from "vue";
import ImgScreen from "~/components/ui/ImgScreen.vue";
import GalleryService from "~/common/services/gallery-service";

const addPluginAttributes = (el, binding, options) => {
  let cursor = "pointer";
  let group = binding.arg || null;
  let openOn;
  let sourceButton;
  let src = el.src; // eslint-disable-line prefer-destructuring
  let title;
  let subtitle;
  let titleUrl;
  let thumbnails;
  let galleryType;
  let withComplement = false;
  let additionalImages = null;
  const events = {};

  if (options.altAsTitle) title = el.alt;

  /* eslint-disable prefer-destructuring */
  openOn = options.openOn;
  sourceButton = options.sourceButton;
  thumbnails = options.thumbnails;
  /* eslint-enable prefer-destructuring */

  if (typeof binding.value !== "undefined") {
    cursor = binding.value.cursor || cursor;
    group = binding.value.group || group;
    openOn = binding.value.openOn || openOn;
    src = binding.value.src || src;
    title = binding.value.title || title;
    subtitle = binding.value.subtitle || subtitle;
    titleUrl = binding.value.titleUrl || titleUrl;
    galleryType = binding.value.galleryType || galleryType;
    withComplement = binding.value.withComplement || withComplement;
    additionalImages = binding.value.additionalImages || additionalImages;

    events.opened = binding.value.opened;
    events.closed = binding.value.closed;
    events.changed = binding.value.changed;

    if (binding.value.sourceButton !== undefined) {
      sourceButton = binding.value.sourceButton; // eslint-disable-line prefer-destructuring
    }
    if (binding.value.thumbnails !== undefined) {
      thumbnails = binding.value.thumbnails; // eslint-disable-line prefer-destructuring
    }
  }

  el.setAttribute("data-vue-img-src", src);

  if (group) el.setAttribute("data-vue-img-group", group);
  if (title) el.setAttribute("data-vue-img-title", title);
  if (subtitle) el.setAttribute("data-vue-img-subtitle", subtitle);
  if (titleUrl) el.setAttribute("data-vue-img-title-url", titleUrl);
  if (sourceButton) el.setAttribute("data-vue-img-source-button", sourceButton);
  if (thumbnails) el.setAttribute("data-vue-img-thumbnails", thumbnails);
  if (galleryType) el.setAttribute("data-vue-img-gallery-type", galleryType);
  if (withComplement)
    el.setAttribute("data-vue-img-with-complement", withComplement);

  if (!src) console.error("v-img element missing src parameter.");

  el.style.cursor = cursor; // eslint-disable-line no-param-reassign

  return {
    cursor,
    src,
    group,
    title,
    subtitle,
    titleUrl,
    events,
    sourceButton,
    openOn,
    thumbnails,
    galleryType,
    withComplement,
    additionalImages,
  };
};

const getCurrentProfile = (store) => {
  if (
    typeof window !== "undefined" &&
    !window.location.pathname.includes("/profile/")
  ) {
    return null;
  }
  if (store.getters && store.getters["profiles/getProfile"]) {
    return store.getters["profiles/getProfile"];
  }
  return null;
};

const install = (Vue, { store, ...options }) => {
  const Screen = Vue.extend(ImgScreen);

  const defaultOptions = {
    altAsTitle: false,
    sourceButton: false,
    thumbnails: false,
    openOn: "click",
    withComplement: false,
  };

  options = Object.assign(defaultOptions, options);

  Vue.directive("img", {
    update(el, binding, vnode, oldVnode) {
      // Disabled mode: caller passes `false` to skip the directive entirely.
      // Used by wrapper components that conditionally need v-img on the same <img>.
      if (binding.value === false) return;

      let altUpdated;
      let srcUpdated;

      if (oldVnode.data.attrs && vnode.data.attrs) {
        srcUpdated = oldVnode.data.attrs.src !== vnode.data.attrs.src;
        if (options.altAsTitle) {
          altUpdated = oldVnode.data.attrs.alt !== vnode.data.attrs.alt;
        }
      }

      const bindingValueUpdated = binding.oldValue !== binding.value;

      if (srcUpdated || altUpdated || bindingValueUpdated) {
        addPluginAttributes(el, binding, options);
      }
    },

    bind(el, binding) {
      // Disabled mode: caller passes `false` to skip the directive entirely.
      // No attributes are set and no click handler is attached. Toggling the
      // value at runtime from `false` to a real object is not supported —
      // callers that need v-img must pass a truthy value at bind time.
      if (binding.value === false) return;

      const addedAttributes = addPluginAttributes(el, binding, options);

      let vm = window.vueImg;
      if (!vm) {
        const element = document.createElement("div");
        element.setAttribute("id", "imageScreen");
        document.querySelector("body").appendChild(element);

        vm = window.vueImg = new Screen({
          store,
        }).$mount("#imageScreen");
      }

      el.addEventListener(addedAttributes.openOn, () => {
        let images;
        if (!el.dataset.vueImgGroup) {
          images = [el];
        } else {
          images = [
            ...document.querySelectorAll(
              `img[data-vue-img-group="${el.dataset.vueImgGroup}"]`
            ),
          ];
        }

        const baseImages = images.map((e) => e.dataset.vueImgSrc);
        const baseTitles = images.map((e) => e.dataset.vueImgTitle);
        const baseSubtitles = images.map((e) => e.dataset.vueImgSubtitle);
        const baseTitleUrls = images.map((e) => e.dataset.vueImgTitleUrl);
        const baseGalleryTypes = images.map((e) => e.dataset.vueImgGalleryType);
        const baseSourceButtons = images.map(
          (e) => e.dataset.vueImgSourceButton === "true"
        );
        const withComplement = el.dataset.vueImgWithComplement === "true";

        const profile = getCurrentProfile(store);

        const galleryData = GalleryService.prepareGalleryData({
          images: baseImages,
          titles: baseTitles,
          subtitles: baseSubtitles,
          titleUrls: baseTitleUrls,
          galleryTypes: baseGalleryTypes,
          sourceButtons: baseSourceButtons,
          profile: profile,
          withComplement: withComplement && profile !== null,
        });

        Vue.set(vm, "images", galleryData.images);
        Vue.set(vm, "titles", galleryData.titles);
        Vue.set(vm, "subtitles", galleryData.subtitles);
        Vue.set(vm, "titleUrls", galleryData.titleUrls);
        Vue.set(vm, "sourceButtons", galleryData.sourceButtons);
        Vue.set(vm, "imageTags", galleryData.imageTags);
        Vue.set(vm, "thumbnails", el.dataset.vueImgThumbnails === "true");
        Vue.set(vm, "currentImageIndex", images.indexOf(el));
        Vue.set(vm, "handlers", addedAttributes.events);
        Vue.set(vm, "closed", false);

        document.querySelector("body").classList.add("body-fs-v-img");
      });
    },
  });
};

export default ({ store }) => {
  Vue.use(install, { store });
};
